import React, { useEffect } from "react";
import PropTypes from "prop-types";

import { DialogCloseIcon } from "../labInvestigations/labIcons";
import ModalPortal from "./ModalPortal";

/**
 * Shared "Delete this entry?" confirmation, px-matched to "Modal · Delete
 * allergy" (3410:2786): 420w r16 #e2e4e9 card with the board's two-layer
 * shadow, a 28px bordered ✕, one line of body copy naming the item and the
 * list it lives in, and an equal-width Cancel / destructive-red "Delete
 * entry" pair. Reuses the .cc-modal-backdrop scrim so it stacks correctly
 * above any parent overlay.
 *
 * `listLabel` is the section-specific noun in the body copy ("allergies",
 * "activity", …) — every section shares this one dialog, so the board's
 * metrics land everywhere at once.
 *
 * Note: the board renders the item name in the SAME run as the sentence (no
 * bold), which is why there is no <strong> here.
 */
const DeleteConfirmDialog = ({
  name,
  listLabel,
  listPhrase,
  submitting,
  onCancel,
  onConfirm,
}) => {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && !submitting && onCancel();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel, submitting]);

  return (
    <ModalPortal>
      <div
        className="cc-modal-backdrop delc-backdrop"
        onMouseDown={submitting ? null : onCancel}
      >
        <div
          className="delc"
          onMouseDown={(e) => e.stopPropagation()}
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="del-confirm-title"
        >
          <header className="delc-head">
            <h2 id="del-confirm-title" className="delc-title">
              Delete this entry?
            </h2>
            <button
              type="button"
              className="delc-x"
              onClick={submitting ? null : onCancel}
              disabled={submitting}
              aria-label="Close"
            >
              <DialogCloseIcon />
            </button>
          </header>
  
          <p className="delc-body">
            This removes {name} from {listPhrase || `the ${listLabel} list`}.
            Deleting is permanent and can&apos;t be undone.
          </p>
  
          <footer className="delc-actions">
            <button
              type="button"
              className="delc-cancel"
              onClick={onCancel}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="button"
              className="delc-del"
              onClick={onConfirm}
              disabled={submitting}
            >
              {submitting ? "Deleting…" : "Delete entry"}
            </button>
          </footer>
        </div>
      </div>
    </ModalPortal>
  );
};

DeleteConfirmDialog.propTypes = {
  name: PropTypes.string.isRequired,
  /** Used to build "the <listLabel> list" — ignored when listPhrase is set. */
  listLabel: PropTypes.string,
  /** Overrides the whole trailing phrase, e.g. "the family history"
   *  (3378:14407 reads "…from the family history.", not "…list."). */
  listPhrase: PropTypes.string,
  submitting: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};

export default DeleteConfirmDialog;
